import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';
import type { CategoryDocType } from '@lancar-pos/db-schema';

export type Category = CategoryDocType;

export function useCategoryStore() {
  const db = useDatabase();
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const subscription = db.categories
      .find({
        selector: { _deleted: false },
        sort: [{ sortOrder: 'asc' }, { name: 'asc' }],
      })
      .$.subscribe((docs: Array<{ toMutableJSON: () => Category }>) => {
        setCategories(docs.map((doc: { toMutableJSON: () => Category }) => doc.toMutableJSON() as Category));
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [db.categories]);

  const addCategory = useCallback(
    async (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt' | '_deleted'>) => {
      const now = new Date().toISOString();
      await db.categories.insert({
        ...category,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        _deleted: false,
      });
    },
    [db.categories]
  );

  const updateCategory = useCallback(
    async (id: string, updates: Partial<Omit<Category, 'id' | 'createdAt'>>) => {
      const doc = await db.categories.findOne(id).exec();
      if (doc) {
        await doc.patch({
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.categories]
  );

  const deleteCategory = useCallback(
    async (id: string) => {
      const doc = await db.categories.findOne(id).exec();
      if (doc) {
        await doc.patch({
          _deleted: true,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.categories]
  );

  const getCategoryById = useCallback(
    (id: string): Category | undefined => {
      return categories.find((c) => c.id === id);
    },
    [categories]
  );

  const getCategoriesByParent = useCallback(
    (parentId: string | null): Category[] => {
      return categories.filter((c) => c.parentId === parentId);
    },
    [categories]
  );

  return {
    categories,
    isLoading,
    addCategory,
    updateCategory,
    deleteCategory,
    getCategoryById,
    getCategoriesByParent,
  };
}

export type CategoryStore = ReturnType<typeof useCategoryStore>;
